import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchApiData } from '../Utils/ApiCalls';
import { CONVERT_TO_ENTERPRISE, ENTERPRISE_INFO, GET_OPTIONAL_FEATURES, UPDATE_ENTERPRISE } from '../urls';
import { Button, Checkbox, Input, Select, showToast, Typography } from '@attentive-platform/stem-ui';

import SearchComponent from './SearchComponent';
import useFormStatus from '../Hooks/useFormStatus';
import {
  CATEGORY_PRICING_TYPES,
  CHECKBOX_FIELDS_BEAM_USER,
  CHECHBOX_FIELDS,
  CONTRACT_INFO_FIELDS,
  dfyModelToCommercialModel,
  EMAIL_REGX,
  FIELDS_TO_REMOVE_FOR_BEAM_USER,
  GROUP_INPUT_DATA,
  INITIAL_FORM_DATA
} from '../Constants/Constants';
import {
  transformContractData,
  transformTradesToSelectedTrades,
  isBeamUser,
  isValidPostalCode,
  normalizeBillingFrequency,
  setContractInfoData,
  setInitialFormData,
  toDateOnly,
  dollarToCents,
  needsSuspendedEarlyAcknowledgement,
  formatAutoCorrectionsAppliedMessage
} from '../Utils/HelperFunctions';
import BeamEntScreen from './UpdateEnterpriseContractBeam/BeamEntScreen';
import { EnterpriseBillingInfo } from './SharedComponent';
import { getEmptyBeamContractPricing, toAllowedDiyAiTradeIds } from '../Utils/beamEnterpriseShared';

const BILLING_TYPE_ITEMS = [
  { value: 'manual', label: 'Manual' },
  { value: 'automated', label: 'Automated' }
];

const SectionTag = ({ type, children }) => {
  const colors = {
    existing: 'bg-gray-100 text-gray-500 border-gray-200',
    new: 'bg-green-50 text-green-700 border-green-200',
    moved: 'bg-blue-50 text-blue-700 border-blue-200'
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded border font-normal ml-2 ${colors[type] || colors.existing}`}>
      {children}
    </span>
  );
};

const FieldWrapper = ({ label, required, help, children }) => (
  <div className='flex flex-col gap-1 mb-3'>
    {label && (
      <Typography variant='body4' color='var(--color-neutral-80)'>
        {label}
        {required && <span className='text-red-500 ml-0.5'>*</span>}
      </Typography>
    )}
    {children}
    {help && (
      <Typography variant='body4' color='var(--color-neutral-50)' sx={{ fontSize: '11.5px', lineHeight: 1.4 }}>
        {help}
      </Typography>
    )}
  </div>
);

const INITIAL_BEAM_DIY_FORM = { diyEnabled: false, diyJobCap: '', diyUsed: 0, allowedTrades: [] };

/** Checkbox row label: snake_case → spaced words with leading letter capitalized. */
const formatCheckboxLabel = field =>
  field.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());

const convertToEnterpriseSuccessMessage = res => {
  if (typeof res.data === 'string' && res.data.trim()) return res.data;
  if (typeof res.data?.message === 'string') return res.data.message;
  return 'Successfully converted to enterprise.';
};

const validateBeamTierSchedule = schedule => {
  for (let i = 0; i < schedule.length; i++) {
    const row = schedule[i];
    const maxSheets = row.max_sheets ?? row.threshold;
    const { units } = row;
    if (maxSheets === '' || maxSheets === undefined || units === '' || units === undefined) return false;
    if (i > 0) {
      const prevMax = schedule[i - 1].max_sheets ?? schedule[i - 1].threshold;
      if (Number(maxSheets) <= Number(prevMax)) return false;
      if (Number(units) < Number(schedule[i - 1].units)) return false;
    }
  }
  return true;
};

/** True when GET enterprise_info returned an active Enterprise company (convert flow should not proceed). */
const isAlreadyEnterpriseFromInfoPayload = data => {
  if (!data || typeof data !== 'object') return false;
  const tier = String(data.summary_tiles?.company_tier ?? '').toLowerCase();
  if (tier.includes('enterprise')) return true;
  const cm = data.commercial_setup?.commercial_model;
  if (cm === null || cm === undefined || cm === '') return false;
  const n = Number(cm);
  if (Number.isFinite(n) && n === 0) return false;
  return true;
};

const SectionCard = ({ title, tag, tagType = 'existing', children, accent }) => {
  const accentBorder = {
    blue: 'border-blue-100 bg-blue-50/20',
    purple: 'border-purple-100 bg-purple-50/10',
    green: 'border-green-100 bg-green-50/20',
    gray: 'border-gray-100 bg-gray-50',
    default: 'border-gray-100'
  };
  return (
    <div className={`border rounded-lg p-4 mb-4 ${accentBorder[accent] || accentBorder.default}`}>
      <div className='flex items-center gap-1 mb-3 pb-2 border-b border-gray-100'>
        <Typography variant='subtitle1-medium' color='var(--color-neutral-80)'>
          {title}
        </Typography>
        {tag && <SectionTag type={tagType}>{tag}</SectionTag>}
      </div>
      {children}
    </div>
  );
};

const EnterpriseConversionForm = ({
  accountSegment,
  companyVertical,
  keyAccountManager,
  onboardingSpecialist,
  updateEnterprise,
  enterpriseAttributes,
  billingDetails,
  enterpriseContract,
  platformAccess,
  email,
  enterpriseOptionalFeatures,
  totalUsers,
  showCategoryPricing,
  setEmail,
  tradeItems,
  setTradeItems
}) => {
  const { successMessage, errorMessage, handleApiResult, setErrorMessage, setSuccessMessage } = useFormStatus();

  const [tradeData, setTradeData] = useState([]);
  const [contractInfo, setContractInfo] = useState({});
  const [formData, setFormData] = useState({});
  const [isManual, setIsManual] = useState(true);
  const [isInvalidZip, setIsInvalidZip] = useState(false);
  const [defaultReport, setDefaultReport] = useState('');
  const [inputValue, setInputValue] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [optionalFeatures, setOptionalFeatures] = useState([]);

  const [beamDfyModel, setBeamDfyModel] = useState('');
  const [beamDfyConcurrency, setBeamDfyConcurrency] = useState('');
  const [beamDfyJobCap, setBeamDfyJobCap] = useState('');
  const [beamTierSchedule, setBeamTierSchedule] = useState([]);
  const [beamShowTierEditor, setBeamShowTierEditor] = useState(false);
  const [beamSwitchConfirmed, setBeamSwitchConfirmed] = useState(false);
  const [beamDiyForm, setBeamDiyForm] = useState(INITIAL_BEAM_DIY_FORM);
  const [beamCategories, setBeamCategories] = useState([]);
  const [beamSelectedCategories, setBeamSelectedCategories] = useState([]);
  const [beamContractPricing, setBeamContractPricing] = useState(() => getEmptyBeamContractPricing());
  const [beamSopConfigured, setBeamSopConfigured] = useState(false);
  const [beamEnterpriseInfoLoaded, setBeamEnterpriseInfoLoaded] = useState(false);
  const [acknowledgeSuspendedEarly, setAcknowledgeSuspendedEarly] = useState(false);

  const isBeam = isBeamUser();

  const tradeTableData = useMemo(() => tradeItems || tradeData, [tradeItems, tradeData]);
  const setTradeTableData = useMemo(() => setTradeItems || setTradeData, [setTradeItems, setTradeData]);

  const showBeamDependentChrome = !isBeam || beamEnterpriseInfoLoaded;

  const requestEnterpriseInfo = useCallback(
    async email => {
      if (!email) {
        showToast('Please enter an email address in the form above', 'error');
        return { ok: false };
      }
      try {
        const response = await fetchApiData({
          endpoint: `${ENTERPRISE_INFO}?email=${encodeURIComponent(email.toLowerCase())}`,
          apiRequest: 'GET'
        });
        if (!updateEnterprise && isAlreadyEnterpriseFromInfoPayload(response.data)) {
          const msg =
            'This company is already on Enterprise. Use Update Enterprise Contract instead of Convert to Enterprise.';
          setSuccessMessage(null);
          setErrorMessage(msg);
          return { ok: false };
        }
        return { ok: true, data: response.data ?? null };
      } catch (e) {
        showToast(e?.message || 'Failed to fetch enterprise details', 'error');
        return { ok: false };
      }
    },
    [updateEnterprise, setErrorMessage, setSuccessMessage]
  );

  const submitsContractAndPlatformDates = isBeam || !isManual;
  const needsSuspendedEarlyAck = useMemo(
    () =>
      submitsContractAndPlatformDates &&
      needsSuspendedEarlyAcknowledgement(contractInfo.platformAccessDate, contractInfo.end_date),
    [submitsContractAndPlatformDates, contractInfo.platformAccessDate, contractInfo.end_date]
  );

  useEffect(() => {
    if (!needsSuspendedEarlyAck) setAcknowledgeSuspendedEarly(false);
  }, [needsSuspendedEarlyAck]);

  const getOptionalFeatures = async () => {
    const response = await fetchApiData({
      endpoint: GET_OPTIONAL_FEATURES,
      apiRequest: 'GET'
    });
    setOptionalFeatures(response?.data);
  };

  useEffect(() => {
    const fetchOptionalFeatures = async () => {
      if (!isBeam) {
        if (updateEnterprise) {
          const response = await fetchApiData({
            endpoint: GET_OPTIONAL_FEATURES,
            apiRequest: 'GET'
          });

          // Merge existing features with new features
          const existingFeatures = enterpriseOptionalFeatures || [];
          const newFeatures = response?.data || [];

          // Create a map of existing features by identifier
          const existingFeaturesMap = new Map(existingFeatures.map(feature => [feature.identifier, feature]));

          // Add new features that don't exist in the current features
          const mergedFeatures = [...existingFeatures];
          newFeatures.forEach(newFeature => {
            if (!existingFeaturesMap.has(newFeature.identifier)) {
              mergedFeatures.push({
                ...newFeature,
                is_selected: false // Default new features to not selected
              });
            }
          });

          setOptionalFeatures(mergedFeatures);
        } else {
          await getOptionalFeatures();
        }
      }
    };
    fetchOptionalFeatures();
  }, [isBeam, updateEnterprise, enterpriseOptionalFeatures]);

  useEffect(() => {
    const initializeFormData = () => {
      const initialData = setInitialFormData({
        email,
        enterpriseAttributes,
        platformAccess,
        accountSegment,
        companyVertical,
        keyAccountManager,
        onboardingSpecialist
      });
      setDefaultReport(initialData?.default_report);
      setFormData({
        ...initialData,
        total_users: totalUsers,
      });
      const selectedOptions = (initialData?.reports_given ?? []).map(report => {
        const label = report?.name;
        const value = report?.id;
        return { label, value };
      });
      setSelectedOptions(selectedOptions);
      setInputValue(selectedOptions.map(option => option?.value));
      if (updateEnterprise) {
        if (!enterpriseAttributes?.is_pilot) {
          // Company is non pilot and company has contract
          // User cannot edit contract details and platform end date
          if (Object.keys(enterpriseContract || {}).length > 0) {
            setIsManual(false);
          }
        }
      }
    };
    const initialiseContractData = () => {
      const initData = setContractInfoData({
        enterpriseContract,
        billingDetails,
        platformAccess
      });
      setContractInfo(initData);
    };
    initializeFormData();
    initialiseContractData();
  }, [
    email,
    enterpriseAttributes,
    platformAccess,
    billingDetails,
    enterpriseContract,
    updateEnterprise,
    totalUsers,
    accountSegment,
    companyVertical,
    keyAccountManager,
    onboardingSpecialist
  ]);

  const handleSubmit = e => {
    updateEnterpriseContract(e);
  };

  const updateEnterpriseContract = async e => {
    e.preventDefault();
    if (!isValidPostalCode(contractInfo.zip, contractInfo.country) && !isManual) {
      window.scrollTo(0, 0);
      setIsInvalidZip(true);
      return;
    }

    if (isBeam && !beamSopConfigured) {
      window.scrollTo(0, 0);
      showToast('SOP must be configured before this conversion can proceed.', 'error');
      return;
    }

    const normalizeEmail = emailString =>
      typeof emailString === 'string' ? emailString.trim().toLowerCase() : '';

    const keyAccountManagerEmail = normalizeEmail(formData.key_account_manager);
    const onboardingSpecialistEmail = normalizeEmail(formData.onboarding_specialist);

    if (keyAccountManagerEmail && !EMAIL_REGX.test(keyAccountManagerEmail)) {
      window.scrollTo(0, 0);
      showToast('Invalid key account manager email', 'error');
      return;
    }
    if (onboardingSpecialistEmail && !EMAIL_REGX.test(onboardingSpecialistEmail)) {
      window.scrollTo(0, 0);
      showToast('Invalid onboarding specialist email', 'error');
      return;
    }

    const suspendedEarlyRequired =
      submitsContractAndPlatformDates &&
      needsSuspendedEarlyAcknowledgement(contractInfo.platformAccessDate, contractInfo.end_date);
    if (suspendedEarlyRequired && !acknowledgeSuspendedEarly) {
      window.scrollTo(0, 0);
      showToast(
        'Platform access ends before the contract end. Confirm suspended-early access below, then submit again (AC-160).',
        'info'
      );
      return;
    }

    if (isBeam) {
      if (!beamDfyModel) {
        window.scrollTo(0, 0);
        showToast('Please select a DFY contract model (usage-based or license-based)', 'error');
        return;
      }
      if (beamDfyModel === 'license_based' && (beamDfyJobCap === '' || Number(beamDfyJobCap) < 0)) {
        window.scrollTo(0, 0);
        showToast('Please enter a valid DFY Job Cap', 'error');
        return;
      }
      if (beamTierSchedule.length > 0 && !validateBeamTierSchedule(beamTierSchedule)) {
        window.scrollTo(0, 0);
        showToast('Tier schedule: thresholds must be strictly increasing, units must be non-decreasing', 'error');
        return;
      }
      if (beamDiyForm.diyEnabled && (beamDiyForm.diyJobCap === '' || Number(beamDiyForm.diyJobCap) < 0)) {
        window.scrollTo(0, 0);
        showToast('Please enter a valid DIY Job Cap', 'error');
        return;
      }
      if (beamDfyModel === 'usage_based') {
        const p = beamContractPricing;
        if (
          p.bpt_contract_amount === '' ||
          p.workable_sheet_cost === '' ||
          p.addendum_sheet_cost === '' ||
          p.manual_worksheet_cost === ''
        ) {
          window.scrollTo(0, 0);
          showToast('Please complete DFY pricing fields for usage-based model', 'error');
          return;
        }
      }
    }

    const newFormData = structuredClone(formData);
    if (isBeam) {
      FIELDS_TO_REMOVE_FOR_BEAM_USER.forEach(field => {
        delete newFormData[field];
      });
    }
    const {
      end_date,
      total_users: formTotalUsers,
      email: formDataEmail,
      account_segment,
      company_vertical,
      ...ent_attributes
    } = newFormData;
    const {
      address,
      city,
      zip,
      country,
      addendum_sheet_cost,
      aerial_contract_amount,
      billing_email,
      billing_frequency,
      bpt_contract_amount,
      company_name,
      manual_per_acre_cost,
      manual_threshold_acres,
      manual_worksheet_cost,
      per_acre_cost,
      remeasurement_contract_amount,
      remeasurement_per_acre_cost,
      start_date: contractStartDate,
      end_date: contractEndDate,
      platformAccessStartDate: contractPlatformAccessStart,
      platformAccessDate: contractPlatformAccessEnd,
      workable_sheet_cost,
      trade_wise_pricing
    } = transformContractData(contractInfo);

    const billingFrequencyForPayload = normalizeBillingFrequency(billing_frequency);

    const platformAccessStart = toDateOnly(contractPlatformAccessStart || '');
    const platformAccessEnd = toDateOnly(contractPlatformAccessEnd || end_date || '');

    let enterprise_contract =
      isBeam || !isManual
        ? {
            addendum_sheet_cost,
            billing_frequency: billingFrequencyForPayload,
            bpt_contract_amount,
            manual_worksheet_cost,
            ...(contractStartDate && { start_date: contractStartDate + 'T00:00:00+05:30' }),
            ...(contractEndDate && { end_date: contractEndDate + 'T00:00:00+05:30' }),
            workable_sheet_cost,
            ...(showCategoryPricing && !isBeam && { trade_wise_pricing }),
            ...(!isBeam && {
              manual_per_acre_cost,
              manual_threshold_acres,
              aerial_contract_amount,
              per_acre_cost,
              remeasurement_contract_amount,
              remeasurement_per_acre_cost
            })
          }
        : null;

    if (isBeam && beamDfyModel === 'usage_based' && enterprise_contract) {
      enterprise_contract = {
        ...enterprise_contract,
        bpt_contract_amount: dollarToCents(beamContractPricing.bpt_contract_amount),
        workable_sheet_cost: dollarToCents(beamContractPricing.workable_sheet_cost),
        addendum_sheet_cost: dollarToCents(beamContractPricing.addendum_sheet_cost),
        manual_worksheet_cost: dollarToCents(beamContractPricing.manual_worksheet_cost),
        billing_frequency: normalizeBillingFrequency(beamContractPricing.billing_frequency),
        trade_wise_pricing: Object.fromEntries(
          Object.entries(beamContractPricing.trade_wise_pricing || {}).map(([key, value]) => [
            key,
            {
              [CATEGORY_PRICING_TYPES.FRESH]: dollarToCents(value[CATEGORY_PRICING_TYPES.FRESH]),
              [CATEGORY_PRICING_TYPES.ADDENDUM]: dollarToCents(value[CATEGORY_PRICING_TYPES.ADDENDUM])
            }
          ])
        )
      };
    }

    const data = {
      acknowledge_suspended_early: Boolean(suspendedEarlyRequired && acknowledgeSuspendedEarly),
      billing_details:
        (isBeam || !isManual) && (company_name || billing_email)
          ? {
              name: company_name,
              address,
              city,
              zip,
              country,
              email: billing_email?.toLowerCase()
            }
          : null,
      email: formDataEmail?.toLowerCase(),
      total_users: parseFloat((formTotalUsers ?? '') || 0),
      enterprise_attributes: {
        ...ent_attributes,
        ...(!isBeam && { reports_given: inputValue, default_report: defaultReport })
      },
      platform_access: {
        ...(platformAccessStart && { start_date: platformAccessStart }),
        ...(platformAccessEnd && { end_date: platformAccessEnd })
      },

      ...(!isBeam && { optional_feature_costs: optionalFeatures }),

      enterprise_contract,
      ...(isBeam || !isManual ? { selected_trades: transformTradesToSelectedTrades(tradeTableData) } : {}),
      account_segment,
      company_vertical,
      key_account_manager: keyAccountManagerEmail ? [keyAccountManagerEmail] : [],
      onboarding_specialist: onboardingSpecialistEmail ? [onboardingSpecialistEmail] : []
    };

    if (isBeam) {
      const commercial_setup = {
        commercial_model: dfyModelToCommercialModel(beamDfyModel),
        confirm_switch_to_license: beamSwitchConfirmed,
        dfy_concurrency_limit: Number(beamDfyConcurrency) || 0,
        diy_enabled: beamDiyForm.diyEnabled,
        diy_job_cap: beamDiyForm.diyEnabled ? Number(beamDiyForm.diyJobCap) : null,
        allowed_diy_ai_trades: beamDiyForm.diyEnabled ? toAllowedDiyAiTradeIds(beamDiyForm.allowedTrades) : []
      };
      if (beamDfyModel === 'license_based') {
        commercial_setup.dfy_job_cap = Number(beamDfyJobCap);
        commercial_setup.dfy_tier_schedule = beamTierSchedule.map(row => ({
          threshold: Number(row.max_sheets ?? row.threshold),
          units: Number(row.units)
        }));
      }
      data.commercial_setup = commercial_setup;
    }

    const res = await fetchApiData({
      endpoint: updateEnterprise ? UPDATE_ENTERPRISE : CONVERT_TO_ENTERPRISE,
      data
    });
    handleApiResult(res);
    if (res.error === null) {
      const correctionMsg = formatAutoCorrectionsAppliedMessage(res.data?.auto_corrections_applied);
      if (correctionMsg) {
        showToast(correctionMsg, 'info');
      }
      if (!updateEnterprise) {
        showToast(convertToEnterpriseSuccessMessage(res), 'success');
      }
      setAcknowledgeSuspendedEarly(false);
      setFormData({ ...INITIAL_FORM_DATA, total_users: 0 });
      setContractInfo(CONTRACT_INFO_FIELDS);
      setTradeTableData([]);
      setIsManual(true);
      setBeamDfyModel('');
      setBeamDfyConcurrency('');
      setBeamDfyJobCap('');
      setBeamTierSchedule([]);
      setBeamShowTierEditor(false);
      setBeamSwitchConfirmed(false);
      setBeamDiyForm({ ...INITIAL_BEAM_DIY_FORM });
      setBeamCategories([]);
      setBeamSelectedCategories([]);
      setBeamContractPricing(getEmptyBeamContractPricing());
      setBeamSopConfigured(false);
      setBeamEnterpriseInfoLoaded(false);
    } else if (res.error) {
      const errStr = String(res.error);
      if (errStr.includes('SUSPENDED_EARLY')) {
        showToast(
          'Platform access ends before the contract end. Confirm suspended-early access below and submit again (AC-160).',
          'info'
        );
      } else {
        showToast(res.error, 'error');
      }
    }
  };

  const disableSubmit = useMemo(() => {
    if (!isBeam) return false;
    if (!beamEnterpriseInfoLoaded) return true;
    const tradesInvalid = tradeTableData.some(item => item.trade === '' || item.workflow.length === 0);
    return tradesInvalid || !beamSopConfigured;
  }, [tradeTableData, isBeam, beamSopConfigured, beamEnterpriseInfoLoaded]);

  return (
    <form onSubmit={handleSubmit} className='flex flex-col gap-4 border p-4 w-full'>
      <>
        <EnterpiseConversion
          successMessage={successMessage}
          errorMessage={errorMessage}
          handleApiResult={handleApiResult}
          isManual={isManual}
          setIsManual={setIsManual}
          formData={formData}
          setFormData={setFormData}
          defaultReport={defaultReport}
          inputValue={inputValue}
          setDefaultReport={setDefaultReport}
          setInputValue={setInputValue}
          setContractInfo={setContractInfo}
          contractInfo={contractInfo}
          updateEnterprise={updateEnterprise}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          isPilot={enterpriseAttributes?.is_pilot}
          isBeam={isBeam}
          optionalFeatures={optionalFeatures}
          setOptionalFeatures={setOptionalFeatures}
          setEmail={setEmail}
          beamDfyModel={beamDfyModel}
          setBeamDfyModel={setBeamDfyModel}
          beamDfyConcurrency={beamDfyConcurrency}
          setBeamDfyConcurrency={setBeamDfyConcurrency}
          beamDfyJobCap={beamDfyJobCap}
          setBeamDfyJobCap={setBeamDfyJobCap}
          beamTierSchedule={beamTierSchedule}
          setBeamTierSchedule={setBeamTierSchedule}
          beamShowTierEditor={beamShowTierEditor}
          setBeamShowTierEditor={setBeamShowTierEditor}
          beamSwitchConfirmed={beamSwitchConfirmed}
          setBeamSwitchConfirmed={setBeamSwitchConfirmed}
          beamDiyForm={beamDiyForm}
          setBeamDiyForm={setBeamDiyForm}
          beamCategories={beamCategories}
          setBeamCategories={setBeamCategories}
          beamSelectedCategories={beamSelectedCategories}
          setBeamSelectedCategories={setBeamSelectedCategories}
          beamContractPricing={beamContractPricing}
          setBeamContractPricing={setBeamContractPricing}
          beamSopConfigured={beamSopConfigured}
          setBeamSopConfigured={setBeamSopConfigured}
          setBeamEnterpriseInfoLoaded={setBeamEnterpriseInfoLoaded}
          setBeamTradeTableData={updateEnterprise ? undefined : setTradeTableData}
          showBeamDependentChrome={showBeamDependentChrome}
          requestEnterpriseInfo={requestEnterpriseInfo}
        />
        {!isManual && !formData?.is_pilot && !isBeam && (
          <EnterpriseBillingInfo
            successMessage={successMessage}
            errorMessage={errorMessage}
            isInvalidZip={isInvalidZip}
            contractInfo={contractInfo}
            setContractInfo={setContractInfo}
            formData={formData}
            setFormData={setFormData}
            isBeam={isBeam}
          />
        )}
      </>

      {needsSuspendedEarlyAck && (
        <div className='flex items-start gap-3 p-3 mb-3 border border-amber-200 bg-amber-50 rounded-lg'>
          <Checkbox
            checked={acknowledgeSuspendedEarly}
            onChange={e => setAcknowledgeSuspendedEarly(e.target.checked)}
          />
          <Typography variant='body4' color='var(--color-neutral-80)' style={{ lineHeight: 1.45 }}>
            Platform access ends before the contract end. Check this box to confirm suspended-early platform access.
          </Typography>
        </div>
      )}

      {showBeamDependentChrome && (
        <div className='text-center mt-4'>
          <Button disabled={disableSubmit} color='primary' size='large' variant='contained' type='submit'>
            {updateEnterprise ? 'Update Contract' : 'Convert to Enterprise'}
          </Button>
        </div>
      )}
    </form>
  );
};

const EnterpiseConversion = ({
  errorMessage,
  successMessage,
  isManual,
  setIsManual,
  formData,
  setFormData,
  setDefaultReport,
  setInputValue,
  setContractInfo,
  contractInfo,
  updateEnterprise,
  selectedOptions,
  setSelectedOptions,
  isPilot,
  defaultReport,
  isBeam,
  optionalFeatures,
  setOptionalFeatures,
  setEmail,
  beamDfyModel,
  setBeamDfyModel,
  beamDfyConcurrency,
  setBeamDfyConcurrency,
  beamDfyJobCap,
  setBeamDfyJobCap,
  beamTierSchedule,
  setBeamTierSchedule,
  beamShowTierEditor,
  setBeamShowTierEditor,
  beamSwitchConfirmed,
  setBeamSwitchConfirmed,
  beamDiyForm,
  setBeamDiyForm,
  beamCategories,
  setBeamCategories,
  beamSelectedCategories,
  setBeamSelectedCategories,
  beamContractPricing,
  setBeamContractPricing,
  beamSopConfigured,
  setBeamSopConfigured,
  setBeamEnterpriseInfoLoaded,
  setBeamTradeTableData,
  showBeamDependentChrome,
  requestEnterpriseInfo
}) => {
  const handleDataChange = (e, field) => {
    if (field === 'is_pilot') {
      setIsManual(true);
    }
    const val = e.target.value;
    setFormData({ ...formData, [field]: val });
  };

  const handleCheckboxChange = (e, field) => {
    setFormData({ ...formData, [field]: e.target.checked });
  };

  return (
    <div className={`${!isBeam ? 'border' : null} p-4 w-full`}>
      {(errorMessage || successMessage) && (
        <div className={`alert ${errorMessage ? 'alert-danger' : 'alert-success'}`} role='alert'>
          {errorMessage || successMessage}
        </div>
      )}
      <div className='input-group mb-3 '>
        <input
          type='Email'
          value={formData?.email}
          onChange={e => {
            if (setEmail) setEmail(e.target.value);
            handleDataChange(e, 'email');
            setContractInfo({ ...contractInfo, email: e.target.value });
          }}
          className='form-control '
          placeholder='Admin Email'
          aria-label='Email'
          required={!updateEnterprise}
        />
      </div>
      {!isBeam && (
        <SearchComponent
          setInputValue={setInputValue}
          setDefaultReport={setDefaultReport}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          defaultReport={defaultReport}
        />
      )}
      {!isBeam &&
        GROUP_INPUT_DATA.map(({ key, inputs, showLabel, className }) => (
          <div className={className} key={key}>
            {inputs.map(({ valueKey, type, label, ariaLabel, options, ...props }) => {
              return (
                <React.Fragment key={valueKey}>
                  {showLabel && <span className='input-group-text'>{label}</span>}
                  {type === 'select' ? (
                    <select
                      value={formData[valueKey] ?? ''}
                      onChange={e => handleDataChange(e, valueKey)}
                      className='form-select'
                      aria-label={ariaLabel}
                      {...props}>
                      {'' in options && (
                        <option key='' value=''>
                          {options['']}
                        </option>
                      )}
                      {Object.entries(options)
                        .filter(([optionValue]) => optionValue !== '')
                        .map(([optionValue, optionLabel]) => (
                          <option key={optionValue} value={optionValue}>
                            {optionLabel}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <input
                      type={type}
                      value={formData[valueKey]}
                      onChange={e => handleDataChange(e, valueKey)}
                      className='form-control'
                      aria-label={ariaLabel}
                      {...props}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ))}
      {isBeam && (
        <BeamEntScreen
          email={formData?.email ?? ''}
          contractInfo={contractInfo}
          setContractInfo={setContractInfo}
          formData={formData}
          setFormData={setFormData}
          dfyModel={beamDfyModel}
          setDfyModel={setBeamDfyModel}
          dfyConcurrency={beamDfyConcurrency}
          setDfyConcurrency={setBeamDfyConcurrency}
          dfyJobCap={beamDfyJobCap}
          setDfyJobCap={setBeamDfyJobCap}
          tierSchedule={beamTierSchedule}
          setTierSchedule={setBeamTierSchedule}
          showTierEditor={beamShowTierEditor}
          setShowTierEditor={setBeamShowTierEditor}
          switchConfirmed={beamSwitchConfirmed}
          setSwitchConfirmed={setBeamSwitchConfirmed}
          diyForm={beamDiyForm}
          setDiyForm={setBeamDiyForm}
          categories={beamCategories}
          setCategories={setBeamCategories}
          selectedCategories={beamSelectedCategories}
          setSelectedCategories={setBeamSelectedCategories}
          contractPricing={beamContractPricing}
          setContractPricing={setBeamContractPricing}
          sopConfigured={beamSopConfigured}
          setSopConfigured={setBeamSopConfigured}
          onEnterpriseInfoLoadedChange={setBeamEnterpriseInfoLoaded}
          setTradeTableData={setBeamTradeTableData}
          updateEnterprise={updateEnterprise}
          requestEnterpriseInfo={requestEnterpriseInfo}
        />
      )}

      {!isBeam && optionalFeatures && optionalFeatures.length > 0 && (
        <div className='border rounded p-4 my-4 bg-white max-h-[260px] overflow-y-scroll'>
          <div className='flex justify-between items-center'>
            <h2 className='text-xl font-semibold'>Features added</h2>
          </div>
          <div className='flex items-center justify-between font-semibold border-b'>
            <div className='flex items-center'>
              <Checkbox
                type='checkbox'
                className='w-4'
                checked={optionalFeatures.every(feature => feature.is_selected)}
                onChange={e => {
                  const updated = optionalFeatures.map(feature => ({
                    ...feature,
                    is_selected: e.target.checked
                  }));
                  setOptionalFeatures(updated);
                }}
              />
              <span className='ml-2'>Features</span>
            </div>
            <div className=''>Pricing</div>
          </div>
          {optionalFeatures.map((feature, index) => (
            <div className='flex items-center justify-between mb-2' key={feature.identifier}>
              <div className='flex items-center'>
                <Checkbox
                  className='w-4'
                  type='checkbox'
                  checked={feature.is_selected}
                  onChange={e => {
                    const updated = [...optionalFeatures];
                    updated[index].is_selected = e.target.checked;
                    setOptionalFeatures(updated);
                  }}
                />
                <span className='ml-2'>{feature.name}</span>
              </div>
              <div className='w-1/4 flex items-center'>
                <Input
                  type='number'
                  value={feature.cost}
                  onChange={e => {
                    const updated = [...optionalFeatures];
                    updated[index].cost = e.target.value;
                    setOptionalFeatures(updated);
                  }}
                />
                <span className='ml-2'>$</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showBeamDependentChrome && (
        <SectionCard title='Other configuration toggles'>
          <div className='flex flex-col gap-2.5 mb-4'>
            {(isBeam ? CHECKBOX_FIELDS_BEAM_USER : CHECHBOX_FIELDS).map(label => (
              <div key={label} className='flex items-center gap-2'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={formData[label]}
                  onChange={e => handleCheckboxChange(e, label)}
                  id={label}
                  disabled={label === 'is_pilot' && !isPilot && updateEnterprise}
                />
                <Typography variant='body4' color='var(--color-neutral-80)'>
                  {formatCheckboxLabel(label)}
                </Typography>
              </div>
            ))}
          </div>

          {!isBeam && (
            <FieldWrapper label='Billing type'>
              <Select
                size='large'
                color='secondary'
                items={formData?.is_pilot ? BILLING_TYPE_ITEMS.filter(i => i.value === 'manual') : BILLING_TYPE_ITEMS}
                value={isManual ? 'manual' : 'automated'}
                onChange={val => setIsManual(val === 'manual')}
                placeholder='Select billing type...'
                variant='std-filled'
              />
            </FieldWrapper>
          )}
        </SectionCard>
      )}
    </div>
  );
};

export default EnterpriseConversionForm;
